import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Eye, X } from 'lucide-react';
import SEO from '../components/SEO';
import './Trabajos.css';

const Trabajos = () => {
  const [galeriaData, setGaleriaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriaActiva, setCategoriaActiva] = useState('todas');
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);

  useEffect(() => {
    const cargarGaleria = async () => {
      try {
        const response = await fetch('/galeria/galeria.json');
        if (response.ok) {
          const data = await response.json();
          setGaleriaData(data);
        }
      } catch (error) {
        console.error('Error cargando galería:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarGaleria();
  }, []);

  const imagenesFiltradas = galeriaData?.imagenes.filter(
    img => categoriaActiva === 'todas' || img.categoria === categoriaActiva
  ) || [];

  const schema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Nuestros Trabajos - Artelaser3D",
    "description": "Galería de trabajos realizados con grabado láser e impresión 3D en Asturias",
    "url": "https://artelaser3d.es/trabajos"
  };

  return (
    <>
      <SEO 
        title="Nuestros Trabajos"
        description="Galería de trabajos realizados con grabado láser e impresión 3D. Descubre proyectos únicos para regalos, decoración, prototipos y más."
        keywords="trabajos grabado láser, trabajos impresión 3D, galería proyectos Asturias, ejemplos grabado láser"
        canonicalUrl="https://artelaser3d.es/trabajos"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="page-hero page-hero--trabajos">
        <div className="page-hero__container">
          <span className="page-hero__badge">
            <Camera size={20} />
            Galería
          </span>
          <h1 className="page-hero__title">
            Nuestros
            <span className="text-primary"> Trabajos</span>
          </h1>
          <p className="page-hero__description">
            {galeriaData?.descripcion || 
              "Descubre algunos de los proyectos que hemos realizado para nuestros clientes. " +
              "Cada pieza es única y está hecha con dedicación y precisión."}
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section section--gallery section--visible">
        <div className="section__container">
          {/* Category Filters */}
          <div className="gallery-filters">
            <button
              className={`gallery-filter-btn ${categoriaActiva === 'todas' ? 'active' : ''}`}
              onClick={() => setCategoriaActiva('todas')}
            >
              Todos
            </button>
            {galeriaData?.categorias.map((cat) => (
              <button
                key={cat.id}
                className={`gallery-filter-btn ${categoriaActiva === cat.id ? 'active' : ''}`}
                onClick={() => setCategoriaActiva(cat.id)}
              >
                {cat.nombre}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="gallery-loading">
              <div className="gallery-spinner"></div>
              <p>Cargando galería...</p>
            </div>
          ) : imagenesFiltradas.length > 0 ? (
            <div className="gallery-grid">
              {imagenesFiltradas.map((imagen, index) => (
                <article 
                  key={index} 
                  className="gallery-item"
                  onClick={() => setImagenSeleccionada(imagen)}
                >
                  <div className="gallery-item__image-wrapper">
                    <img 
                      src={imagen.src} 
                      alt={imagen.titulo}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                    <div className="gallery-item__overlay">
                      <Eye size={32} />
                      <span>Ver detalle</span>
                    </div>
                  </div>
                  <div className="gallery-item__info">
                    <span className="gallery-item__category">
                      {galeriaData?.categorias.find(c => c.id === imagen.categoria)?.nombre}
                    </span>
                    <h3>{imagen.titulo}</h3>
                    <p>{imagen.descripcion}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="gallery-empty">
              <Camera size={64} />
              <h3>Galería en construcción</h3>
              <p>
                Estamos preparando nuestra galería con los mejores trabajos. 
                ¡Vuelve pronto para verlos!
              </p>
              <div className="gallery-empty__instructions">
                <h4>¿Cómo añadir imágenes?</h4>
                <ol>
                  <li>Sube tus imágenes a la carpeta <code>/public/galeria/laser/</code> o <code>/public/galeria/impresion3d/</code></li>
                  <li>Edita el archivo <code>/public/galeria/galeria.json</code></li>
                  <li>Añade cada imagen al array "imagenes" con su información</li>
                  <li>¡Listo! Las imágenes aparecerán automáticamente</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--cta section--cta--trabajos section--visible">
        <div className="cta__content">
          <h2 className="cta__title--trabajos">¿Tienes un proyecto en mente?</h2>
          <p className="cta__description--trabajos">
            Cada trabajo que ves aquí comenzó como una idea. 
            La tuya puede ser la próxima. Cuéntanos qué necesitas.
          </p>
          <div className="cta__buttons">
            <Link to="/proyecto-personalizado" className="btn btn--primary btn--lg">
              Iniciar Mi Proyecto
            </Link>
            <Link to="/contacto" className="btn btn--outline btn--lg">
              Contactar
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {imagenSeleccionada && (
        <div 
          className="gallery-lightbox"
          onClick={() => setImagenSeleccionada(null)}
        >
          <button 
            className="gallery-lightbox__close"
            onClick={() => setImagenSeleccionada(null)}
            aria-label="Cerrar"
          >
            <X size={32} />
          </button>
          <div 
            className="gallery-lightbox__content"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={imagenSeleccionada.src} 
              alt={imagenSeleccionada.titulo}
            />
            <div className="gallery-lightbox__info">
              <span className="gallery-lightbox__category">
                {galeriaData?.categorias.find(c => c.id === imagenSeleccionada.categoria)?.nombre}
              </span>
              <h3>{imagenSeleccionada.titulo}</h3>
              <p>{imagenSeleccionada.descripcion}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Trabajos;
